import { ArrowDown, ArrowUp, Search } from "lucide-react"
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { useFormatNumber, useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"

export interface SheetColumn<T> {
  readonly id: string
  readonly label: string
  /** The sortable, filterable value; null sorts last. */
  readonly value: (row: T) => string | number | null
  readonly render?: (row: T) => ReactNode
  /** What people read (a formatted date); filtering matches it too, and the raw value is the tooltip. */
  readonly display?: (row: T) => string | null
  readonly numeric?: boolean
  readonly mono?: boolean
  /** A fixed CSS width (`8rem`); the rest share the space. */
  readonly width?: string
}

const SHOWN = 200

/**
 * A cell's output as a spreadsheet: row numbers, sortable headers, a filter, a fixed-height scroll
 * with a sticky header, and keyboard movement (↑ ↓ to move, Enter to open, / to filter).
 */
export function Sheet<T>({
  rows,
  columns,
  rowKey,
  label,
  onOpen,
  activeKey,
  onActive,
  rowTone,
  empty,
  toolbar,
}: {
  rows: readonly T[]
  columns: readonly SheetColumn<T>[]
  rowKey: (row: T) => string
  label: string
  onOpen?: (row: T) => void
  activeKey?: string | null
  onActive?: (row: T) => void
  rowTone?: (row: T) => "warn" | "bad" | "muted" | undefined
  empty: ReactNode
  toolbar?: ReactNode
}) {
  const t = useT()
  const formatNumber = useFormatNumber()
  const [query, setQuery] = useState("")
  const [sort, setSort] = useState<{ id: string; dir: 1 | -1 } | null>(null)
  const [all, setAll] = useState(false)
  const [localActive, setLocalActive] = useState<string | null>(null)
  const active = activeKey !== undefined ? activeKey : localActive
  const filterRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  const view = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q ? rows.filter((r) => columns.some((c) => [c.value(r), c.display?.(r)].some((v) => String(v ?? "").toLowerCase().includes(q)))) : [...rows]
    if (sort) {
      const col = columns.find((c) => c.id === sort.id)
      if (col)
        filtered.sort((a, b) => {
          const x = col.value(a)
          const y = col.value(b)
          if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1
          return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * sort.dir
        })
    }
    return filtered
  }, [rows, columns, query, sort])
  const shown = all ? view : view.slice(0, SHOWN)

  // Keep the active row in view as the keyboard moves it.
  useEffect(() => {
    if (!active) return
    bodyRef.current?.querySelector<HTMLElement>(`[data-row="${CSS.escape(active)}"]`)?.scrollIntoView({ block: "nearest" })
  }, [active])

  const move = (delta: number) => {
    if (!shown.length) return
    const at = shown.findIndex((r) => rowKey(r) === active)
    const next = shown[Math.min(shown.length - 1, Math.max(0, at === -1 ? 0 : at + delta))]
    if (!next) return
    setLocalActive(rowKey(next))
    onActive?.(next)
  }
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLInputElement) return
    if (e.key === "ArrowDown" || e.key === "j") {
      e.preventDefault()
      move(1)
    } else if (e.key === "ArrowUp" || e.key === "k") {
      e.preventDefault()
      move(-1)
    } else if (e.key === "Enter" && onOpen) {
      const row = shown.find((r) => rowKey(r) === active)
      if (row) onOpen(row)
    } else if (e.key === "/") {
      e.preventDefault()
      filterRef.current?.focus()
    }
  }

  const template = `3rem ${columns.map((c) => c.width ?? "minmax(7rem,1fr)").join(" ")}`
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative block w-full sm:w-64">
          <span className="sr-only">{t("wb.sheet.filter")}</span>
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-subtle-foreground" aria-hidden />
          <input
            ref={filterRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setQuery("")
                bodyRef.current?.focus()
              }
            }}
            placeholder={t("wb.sheet.filter")}
            className="h-8 w-full rounded-lg border border-input bg-background ps-8 pe-2 text-[13px] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
          />
        </label>
        <span className="text-xs text-muted-foreground tabular-nums">
          {query ? t("wb.sheet.filtered", { shown: formatNumber(view.length), total: formatNumber(rows.length) }) : t("wb.sheet.rows", { count: rows.length })}
        </span>
        <div className="ms-auto flex items-center gap-2">{toolbar}</div>
      </div>
      <div
        ref={bodyRef}
        role="grid"
        aria-label={label}
        aria-rowcount={view.length + 1}
        tabIndex={0}
        onKeyDown={onKey}
        className="max-h-[26rem] overflow-auto rounded-xl border border-border bg-card text-[13px] outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
      >
        <div role="row" className="sticky top-0 z-10 grid min-w-max border-b border-border bg-surface-subtle" style={{ gridTemplateColumns: template }}>
          <span role="columnheader" className="px-2 py-2 text-end text-2xs text-subtle-foreground">
            #
          </span>
          {columns.map((c) => {
            const dir = sort?.id === c.id ? sort.dir : 0
            return (
              <button
                key={c.id}
                type="button"
                role="columnheader"
                aria-sort={dir === 1 ? "ascending" : dir === -1 ? "descending" : "none"}
                onClick={() => setSort(dir === 0 ? { id: c.id, dir: 1 } : dir === 1 ? { id: c.id, dir: -1 } : null)}
                className={cn("flex items-center gap-1 truncate border-s border-border px-2.5 py-2 text-xs font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground", c.numeric && "justify-end")}
              >
                {c.label}
                {dir === 1 ? <ArrowUp className="size-3" aria-hidden /> : dir === -1 ? <ArrowDown className="size-3" aria-hidden /> : null}
              </button>
            )
          })}
        </div>
        {shown.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">{query ? t("wb.sheet.noMatch") : empty}</div>
        ) : (
          shown.map((row, i) => {
            const key = rowKey(row)
            const tone = rowTone?.(row)
            return (
              <div
                key={key}
                role="row"
                data-row={key}
                aria-selected={key === active}
                onClick={() => {
                  setLocalActive(key)
                  onActive?.(row)
                }}
                onDoubleClick={() => onOpen?.(row)}
                className={cn(
                  "grid min-w-max cursor-default border-b border-border/70 last:border-b-0 hover:bg-muted/40",
                  key === active && "bg-[var(--lineage)]/10 hover:bg-[var(--lineage)]/15",
                  tone === "muted" && "text-muted-foreground",
                )}
                style={{ gridTemplateColumns: template }}
              >
                <span className={cn("border-s-2 border-transparent px-2 py-1.5 text-end text-2xs text-subtle-foreground tabular-nums", tone === "warn" && "border-s-warning", tone === "bad" && "border-s-destructive")}>
                  {i + 1}
                </span>
                {columns.map((c) => (
                  <span key={c.id} role="gridcell" dir="auto" className={cn("truncate border-s border-border/50 px-2.5 py-1.5", c.numeric && "text-end tabular-nums", c.mono && "font-mono text-xs")}>
                    {c.render ? c.render(row) : c.display ? <Shown text={c.display(row)} raw={c.value(row)} /> : (c.value(row) ?? <span className="text-subtle-foreground">—</span>)}
                  </span>
                ))}
              </div>
            )
          })
        )}
      </div>
      {view.length > shown.length ? (
        <button type="button" onClick={() => setAll(true)} className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          {t("wb.sheet.showAll", { count: view.length })}
        </button>
      ) : null}
    </div>
  )
}

function Shown({ text, raw }: { text: string | null; raw: string | number | null }) {
  if (text === null) return <span className="text-subtle-foreground">—</span>
  return <span title={raw === null || String(raw) === text ? undefined : String(raw)}>{text}</span>
}
